import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setProgress, setToast } from "../../../redux/slice/system";
import { setDiagramDateRange } from "../../../redux/slice/diagram";
import { useAbortedEffect, useRawTable } from "../../hooks";
import { CommonAPI } from "../../../axios/api";
import moment from "moment";

export default function Module_ElderlyHealth_ElderlyHealthVisitMonthly({
  moduleTitle = "",
  moduleData = {},
  apiData,
  setReload,
}) {
  const dispatch = useDispatch();
  const redux_diagram = useSelector((state) => state.diagram);
  const { setTbData, renderTable } = useRawTable({
    setting: moduleData,
    nullTransfer: "-",
    subTitleBgColor: "elderlytitle1",
  });

  useEffect(() => {
    if (
      redux_diagram.dateRangeSetting.ElderlyHealthVisitMonthly ||
      redux_diagram.dateRangeSetting.ElderlyHealthVisitMonthly === null
    )
      return;

    let thisYear = Number(moment().format("YYYY"));
    let filterData = {
      ...redux_diagram.dateRangeSetting,
      ElderlyHealthVisitMonthly: [
        {
          key: "填寫日期",
          start: moment().startOf("year").format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        },
      ],
    };
    dispatch(setDiagramDateRange(filterData));
  }, []);

  useAbortedEffect(
    async (signal) => {
      if (
        !apiData ||
        (!redux_diagram.dateRangeSetting.ElderlyHealthVisitMonthly &&
          redux_diagram.dateRangeSetting.ElderlyHealthVisitMonthly !== null)
      )
        return;

      let writeDate = {
        ...(redux_diagram.dateRangeSetting.ElderlyHealthVisitMonthly.find(
          (item) => item.key === "填寫日期"
        ) ?? {
          start: moment().startOf("year").format("YYYY-MM-DD"),
          end: moment().format("YYYY-MM-DD"),
        }),
      };

      if (writeDate.start)
        writeDate.start = moment(writeDate.start).format("YYYY-MM-DD");
      if (writeDate.end)
        writeDate.end = moment(writeDate.end).format("YYYY-MM-DD");

      let result_dead = await CommonAPI.getRoot({
        options: { signal },
        path: `/getMatsuIsDead?startDate=${writeDate.start}&endDate=${writeDate.end}`,
      });
      result_dead.data = {
        raw: result_dead.data,
        format: result_dead.data.reduce((pre, cur) => {
          let temp = { ...pre };
          if (!temp[cur.RegionName]) temp[cur.RegionName] = {};
          temp[cur.RegionName][cur.VillageName ? cur.VillageName : "其他"] =
            cur.CNT;

          temp[cur.RegionName]["小計"] =
            (temp[cur.RegionName]["小計"] ?? 0) + cur.CNT;
          return temp;
        }, {}),
      };
      result_dead.data.format["總計"] = result_dead.data.raw.reduce(
        (pre, cur) => (pre += cur.CNT),
        0
      );

      let result_live = await CommonAPI.getRoot({
        options: { signal },
        path: `/getMatsuLiveLongTerm?endDate=${writeDate.end}`,
      });
      result_live.data = {
        raw: result_live.data,
        format: result_live.data.reduce((pre, cur) => {
          let temp = { ...pre };
          if (!temp[cur.RegionName]) temp[cur.RegionName] = {};
          temp[cur.RegionName][cur.VillageName ? cur.VillageName : "其他"] =
            cur.CNT;

          temp[cur.RegionName]["小計"] =
            (temp[cur.RegionName]["小計"] ?? 0) + cur.CNT;

          return temp;
        }, {}),
      };
      result_live.data.format["總計"] = result_live.data.raw.reduce(
        (pre, cur) => (pre += cur.CNT),
        0
      );

      let result_household = await CommonAPI.getRoot({
        options: { signal },
        path: `/getHealthCheckHousehold?endDate=${writeDate.end}`,
      });
      if (result_household.Response === 1) {
        result_household.data = result_household.data.map((item) => ({
          ...item,
          RegionName: String(item.ADDRZIP).slice(3, 6),
          VillageName: item.ADDRAREA,
        }));

        let formatData = formatAction({
          writeDate,
          data: apiData.filter((item) => {
            let diffYear = Number(
              moment(new Date()).diff(item["填寫日期"], "years")
            );
            return item["年齡"] && item["年齡"] - diffYear >= 65;
          }),
          household: result_household.data,
        }).map((item) => ({
          ...item,
          dead: !item.RegionName
            ? result_dead.data.format["總計"]
            : result_dead.data.format[item.RegionName]?.[item.VillageName] ??
              (item.dead !== undefined ? 0 : null),
          inAgency: !item.RegionName
            ? result_live.data.format["總計"]
            : result_live.data.format[item.RegionName]?.[item.VillageName] ??
              (item.inAgency !== undefined ? 0 : null),
        }));

        setTbData({
          raw: formatData,
          format: null,
          export: null,
        });
        setReload(false);
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: result_household.message,
            detail: "",
          })
        );
      }
      dispatch(setProgress({ show: false }));
    },
    [apiData]
  );

  const formatAction = ({ writeDate, data, household }) => {
    // 以鄉分組
    let tempData = {};
    data.forEach((item) => {
      item.VillageName =
        item["村里"] === "null" || !item["村里"] ? "其他" : item["村里"];

      if (!tempData[item["鄉鎮"]]) tempData[item["鄉鎮"]] = {};
      if (!tempData[item["鄉鎮"]][item.VillageName])
        tempData[item["鄉鎮"]][item.VillageName] = {
          data: [],
          inVillage: 0,
        };
      tempData[item["鄉鎮"]][item.VillageName].data.push(item);
    });

    household.forEach((item) => {
      item.VillageName =
        item.VillageName === "null" || !item.VillageName
          ? "其他"
          : item.VillageName;

      if (!tempData[item.RegionName]) tempData[item.RegionName] = {};
      if (!tempData[item.RegionName][item.VillageName])
        tempData[item.RegionName][item.VillageName] = {
          data: [],
          inVillage: 0,
        };
      tempData[item.RegionName][item.VillageName].inVillage = item.CNT;
    });

    Object.entries(tempData).forEach(([regionKey, regionData]) => {
      let subTotal = {
        inVillage: 0,
        visitCount: 0,
        inAgency: 0,
        dead: 0,
        level1: 0,
        level2: 0,
        level3: 0,
        visit_percent: 0,
        unVisitCount: 0,
        unVisit_percent: 0,
        total_level: 20,
        level1_rate: 0,
        level2_rate: 0,
        level3_rate: 0,
        isSkip: true,
        isFoot: true,
      };

      Object.entries(tempData[regionKey]).forEach(
        ([villageKey, villageData]) => {
          let inAgency = 0, // 入住機構
            dead = 0; // 死亡
          villageData.data = villageData.data
            .filter(
              (item) =>
                new Date(item["填寫日期"]) >= new Date(writeDate.start) &&
                new Date(item["填寫日期"]) <= new Date(writeDate.end)
            )
            .reduce(
              (pre, cur) => ({
                ...pre,
                [cur["個案ID"]]: cur,
              }),
              {}
            );
          villageData.data = Object.values(villageData.data);

          tempData[regionKey][villageKey] = {
            inVillage: villageData.inVillage, // 65歲以上在籍人數
            ...villageData.data.reduce((pre, cur) => {
              // 已完訪人數
              pre.visitCount = (pre.visitCount ?? 0) + 1;
              // 居住本縣機構人數
              pre.inAgency =
                (pre.inAgency ?? 0) + (cur["機構註記欄位"] === "1" ? 1 : 0);
              // 死亡
              pre.dead =
                (pre.dead ?? 0) +
                (cur["死亡註記"] === "1" &&
                new Date(cur["死亡日期"]) >= new Date(writeDate.start) &&
                new Date(cur["死亡日期"]) <= new Date(writeDate.end)
                  ? 1
                  : 0);
              // 健康
              pre.level1 =
                (pre.level1 ?? 0) +
                (["健康訪視", "衰弱前期訪視"].includes(cur["列管級數"])
                  ? 1
                  : 0);
              // 衰弱
              pre.level2 =
                (pre.level2 ?? 0) +
                (["衰弱訪視"].includes(cur["列管級數"]) ? 1 : 0);
              // 失能
              pre.level3 =
                (pre.level3 ?? 0) +
                (["失能非長照訪視", "失能長照訪視"].includes(cur["列管級數"])
                  ? 1
                  : 0);

              return pre;
            }, {}),
          };

          let data = tempData[regionKey][villageKey],
            visit_percent =
              data.visitCount && data.inVillage
                ? Math.round((data.visitCount / data.inVillage) * 100)
                : 0,
            unVisitCount = data.inVillage - data.visitCount,
            unVisit_percent =
              unVisitCount && data.inVillage
                ? Math.round((unVisitCount / data.inVillage) * 100)
                : 0,
            total_level =
              (data.level1 ?? 0) + (data.level2 ?? 0) + (data.level3 ?? 0),
            level1_rate =
              !data.level1 && !data.level2 && !data.level3
                ? "0%"
                : 100 -
                  (data.level2
                    ? Math.round((data.level2 / total_level) * 100)
                    : 0) -
                  (data.level3
                    ? Math.round((data.level3 / total_level) * 100)
                    : 0) +
                  "%",
            level2_rate =
              (data.level2
                ? Math.round((data.level2 / total_level) * 100)
                : "0") + "%",
            level3_rate =
              (data.level3
                ? Math.round((data.level3 / total_level) * 100)
                : "0") + "%";

          tempData[regionKey][villageKey] = {
            ...tempData[regionKey][villageKey],
            visit_percent,
            unVisitCount,
            unVisit_percent,
            total_level,
            level1_rate,
            level2_rate,
            level3_rate,
          };

          // 小計
          subTotal.inVillage += tempData[regionKey][villageKey].inVillage ?? 0;
          subTotal.visitCount +=
            tempData[regionKey][villageKey].visitCount ?? 0;
          subTotal.inAgency += tempData[regionKey][villageKey].inAgency ?? 0;
          subTotal.dead += tempData[regionKey][villageKey].dead ?? 0;
          subTotal.level1 += tempData[regionKey][villageKey].level1 ?? 0;
          subTotal.level2 += tempData[regionKey][villageKey].level2 ?? 0;
          subTotal.level3 += tempData[regionKey][villageKey].level3 ?? 0;
          subTotal.visit_percent =
            subTotal.visitCount && subTotal.inVillage
              ? Math.round((subTotal.visitCount / subTotal.inVillage) * 100)
              : 0;
          subTotal.unVisitCount = subTotal.inVillage - subTotal.visitCount;
          subTotal.unVisit_percent =
            subTotal.unVisitCount && subTotal.inVillage
              ? Math.round((subTotal.unVisitCount / subTotal.inVillage) * 100)
              : 0;

          subTotal.total_level =
            (subTotal.level1 ?? 0) +
            (subTotal.level2 ?? 0) +
            (subTotal.level3 ?? 0);
          subTotal.level1_rate =
            !subTotal.level1 && !subTotal.level2 && !subTotal.level3
              ? "0%"
              : 100 -
                (subTotal.level2
                  ? Math.round((subTotal.level2 / subTotal.total_level) * 100)
                  : 0) -
                (subTotal.level3
                  ? Math.round((subTotal.level3 / subTotal.total_level) * 100)
                  : 0) +
                "%";
          subTotal.level2_rate =
            (subTotal.level2
              ? Math.round((subTotal.level2 / subTotal.total_level) * 100)
              : "0") + "%";
          subTotal.level3_rate =
            (subTotal.level3
              ? Math.round((subTotal.level3 / subTotal.total_level) * 100)
              : "0") + "%";
        }
      );

      tempData[regionKey]["小計"] = subTotal;
    });

    let formatData = Object.entries(tempData).reduce(
        (preMain, [RegionKey]) => [
          ...preMain,
          ...Object.entries(tempData[RegionKey]).map(
            ([VillageKey, VillageData], index) => ({
              RegionName: RegionKey,
              VillageName: VillageKey,
              rowSpan_RegionName:
                index === 0 ? Object.keys(tempData[RegionKey]).length : null,
              ...VillageData,
            })
          ),
        ],
        []
      ),
      sumData = formatData.reduce((pre, cur) => {
        if (!!cur.isSkip) return pre;
        return {
          ...pre,
          inVillage: (pre.inVillage ?? 0) + (cur.inVillage ?? 0),
          visitCount: (pre.visitCount ?? 0) + (cur.visitCount ?? 0),
          unVisitCount: (pre.unVisitCount ?? 0) + (cur.unVisitCount ?? 0),
          inAgency: (pre.inAgency ?? 0) + (cur.inAgency ?? 0),
          dead: (pre.dead ?? 0) + (cur.dead ?? 0),
          level1: (pre.level1 ?? 0) + (cur.level1 ?? 0),
          level2: (pre.level2 ?? 0) + (cur.level2 ?? 0),
          level3: (pre.level3 ?? 0) + (cur.level3 ?? 0),
          total_level:
            (pre.total_level ?? 0) +
            (cur.level1 ?? 0) +
            (cur.level2 ?? 0) +
            (cur.level3 ?? 0),
        };
      }, {});
    sumData.unVisitCount = sumData.inVillage - sumData.visitCount;
    sumData = {
      ...sumData,
      VillageName: "總計",
      visit_percent:
        sumData.visitCount && sumData.inVillage
          ? Math.round((sumData.visitCount / sumData.inVillage) * 100)
          : 0,
      unVisit_percent:
        sumData.unVisitCount && sumData.inVillage
          ? Math.round((sumData.unVisitCount / sumData.inVillage) * 100)
          : 0,
      level1_rate:
        Math.round((sumData.level1 / sumData.total_level) * 100) + "%",
      level2_rate:
        Math.round((sumData.level2 / sumData.total_level) * 100) + "%",
      level3_rate:
        Math.round((sumData.level3 / sumData.total_level) * 100) + "%",
    };
    sumData.level1_rate =
      100 -
      Math.round((sumData.level2 / sumData.total_level) * 100) -
      Math.round((sumData.level3 / sumData.total_level) * 100) +
      "%";
    sumData.isFoot = true;

    return [sumData, ...formatData];
  };

  return (
    <>
      <div className="w-full overflow-auto">
        <div>
          {renderTable({
            moduleTitle: `${moduleData.label}`,
          })}
        </div>
      </div>
    </>
  );
}
